import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { IncidentClockPill } from "./IncidentClockPill";

export function IncidentClockPanel({ record, onUpdated }) {
  const { isRole, investigationRole } = useAuth();
  const c = record.clock;
  const canExtend = (isRole("admin") || investigationRole === "lead_investigator") && c && !c.stopped && c.due < c.max_due;
  const [open, setOpen] = useState(false);
  const [due, setDue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!c) return null;

  const submit = async () => {
    if (!due || !reason.trim()) { toast.error("Pick a new due date and give a reason."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${record.id}/extend`, { new_due_date: due, reason });
      toast.success(`Deadline extended to ${data.clock.due}.`);
      setOpen(false); setDue(""); setReason("");
      onUpdated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Extension failed."); }
    setBusy(false);
  };

  return (
    <div className="border border-border p-3 space-y-2" data-testid="incident-clock-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-semibold text-sm">Determination clock (60 days, extendable to 90)</h4>
        <IncidentClockPill clock={c} testId="incident-clock-pill" />
      </div>
      <p className="text-xs text-muted-foreground">
        Discovered {c.start} · standard due {c.base_due} · hard limit {c.max_due}. Evaluation, investigation and determination
        must be complete by the due date; the clock stops once a resolution date is entered or the incident is closed.
      </p>
      {c.extensions.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="incident-extension-history">
          {c.extensions.map((x, i) => (
            <li key={i}><span className="font-medium">{x.from} → {x.to}</span> by {x.by} on {(x.at || "").slice(0, 10)}: {x.reason}</li>
          ))}
        </ul>
      )}
      {canExtend && !open && (
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => setOpen(true)} data-testid="incident-extend-btn">Extend deadline</Button>
      )}
      {open && (
        <div className="space-y-2 bg-muted/40 p-2" data-testid="incident-extend-form">
          <div>
            <Label className="text-xs">New due date (max {c.max_due})</Label>
            <Input type="date" min={c.due} max={c.max_due} value={due} onChange={(e) => setDue(e.target.value)} className="rounded-none w-48" data-testid="incident-extend-date" />
          </div>
          <div>
            <Label className="text-xs">Reason *</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none" data-testid="incident-extend-reason" placeholder="Why more time is needed (e.g. awaiting vendor logs, interviews pending)" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-none" onClick={submit} disabled={busy} data-testid="incident-extend-save">{busy ? "Saving…" : "Save extension"}</Button>
            <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
